import ProjectsAPI from "../../api/projects/ProjectsAPI.js";
import { SectionTypesEnum } from "../../common/enums.js";
import ProjectsApi from "../../api/projects/ProjectsAPI.js";
import { PortfolioActions } from "./PortfolioActions.js";

function updatePageInList({ getters, commit }) {
    if (!getters.isCaseStudy) {
        commit("updateEditedPageSectionsInPortfolioPages");
    } else {
        commit("updateEditedCaseStudySectionsInCaseStudyPages");
    }
}

async function updateNavSection(context, compromisedIndex, newSection) {
    const { state, dispatch, commit } = context;
    if (!state.project) return;
    const projectId = state.project._id;

    let allowUpdate = false;
    const processSections = state.project.sections.filter(
        (s) =>
            s.type === SectionTypesEnum.PROCESS &&
            !s.deletedAt
    );
    if (processSections.length === 0) return;

    processSections.forEach((processSection) => {
        const navItems = processSection.processItems || [];

        if (!newSection && !!navItems.find((i) => i.sectionIndex === compromisedIndex)) {
            state.selectedNavStep = navItems.findIndex((i) => i.sectionIndex === compromisedIndex);
            state.selectedSection = processSection;
            commit("showAlertMessage", {
                type: "success",
                message: `Linked section ${!newSection ? "deleted" : "changed"}.`,
                interval: 60,
            });
            navItems.find((i) => i.sectionIndex === compromisedIndex).sectionIndex = null;

            allowUpdate = true;
        } else if (
            newSection &&
            !!navItems.findLast((e) => !!e.sectionIndex) &&
            compromisedIndex <= navItems.findLast((e) => !!e.sectionIndex).sectionIndex
        ) {
            navItems.forEach((i) => {
                if (compromisedIndex <= i.sectionIndex) i.sectionIndex = i.sectionIndex + 1;
            });

            allowUpdate = true;
        }
    });

    if (allowUpdate) {
        await dispatch("updateSections", { projectId, sections: processSections });
    }
}

const SectionActions = {
    async addSection(context, { postData, modal = null }) {
        const { state, commit, getters } = context;

        try {
            const response = await ProjectsAPI.addSection(state.project._id, postData);
            commit("replaceProject", response.data);
            const newSection = response.data.sections[postData.index];
            commit("selectSection", newSection);
            setTimeout(() => {
                commit("scrollToSection", { sectionId: newSection?._id });
            }, 10);
        } catch (error) {
            console.error(error);
        } finally {
            updatePageInList(context);
            await updateNavSection(context, postData.index, true);
            commit("setActiveModal", modal || null);
        }
    },

    async updateSection(context, { section, modal = null }) {
        const { state, commit, getters } = context;
        if (getters.isEditorBlocked) return;

        if (section?.type === SectionTypesEnum.HERO) {
            commit("updatePortfolioHero", section);
            PortfolioActions.updatePortfolio("Update portfolio hero", true);
            return;
        }

        if (section?.type === SectionTypesEnum.FOOTER) {
            commit("updatePortfolioFooter", section);
            PortfolioActions.updatePortfolio("Update portfolio footer", true);
            return;
        }

        commit("updateSavingState");
        try {
            await ProjectsAPI.updateSection(state.project._id, section);
            commit("updateSection", section);
        } catch (error) {
            console.error(error);
            const errorObject = error.response?.data;
            if (error.response?.status === 405 && errorObject?.code === 1014) {
                if (state.userSettings?.currentSubscription?.status === 'past_due') {
                    console.log("BLOCKED from: SectionActions");
                    commit("setBlockedEditor", true);
                }
            }
        } finally {
            updatePageInList(context);
            commit("updateSavingState");
            if (
                (modal && modal === "keepModalOpen") ||
                state.activeModal === "SubscriptionExpiredModal"
            ) {
                return;
            }
            commit("setActiveModal", modal || null);
        }
    },

    async updateSocialLinks(context, { socialLinks, trackLabel = null }) {
        const { commit, getters } = context;
        if (getters.isEditorBlocked) return;

        commit("setPortfolioHeroSocialIcons", { socialIcons: socialLinks });
        await PortfolioActions.updatePortfolioHero(context.state.portfolio.portfolioHero);
    },

    async updateSections(context, { projectId, sections }) {
        const { commit, getters } = context;
        if (getters.isEditorBlocked) return;

        if (!projectId || !Array.isArray(sections)) return;

        commit("updateSavingState");
        try {
            await ProjectsAPI.updateSections(projectId, sections);
        } catch (error) {
            console.error(error);
            const errorObject = error.response?.data;
            if (error.response?.status === 405 && errorObject?.code === 1014) {
                if (context.state.userSettings?.currentSubscription?.status === 'past_due') {
                    console.log("BLOCKED from: SectionActions");
                    commit("setBlockedEditor", true);
                }
            }
        } finally {
            commit("updateSavingState");
        }
    },

    async archiveSection(context, { projectId, sectionIndex }) {
        const { state, commit } = context;
        try {
            const response = await ProjectsAPI.archiveSection(projectId, sectionIndex);
            state.project.sections[sectionIndex].deletedAt = response.data.deletedAt;
            state.selectedSection = null;
        } catch (error) {
            console.error(error);
        } finally {
            if (!state.project.sections[sectionIndex].deletedAt) {
                commit("scrollToSection", { sectionId: state.project.sections[sectionIndex]?._id });
            }
            commit("setActiveModal", null);
            await updateNavSection(context, sectionIndex, false);
        }
    },

    async reorderSections(context, sections, keepModalOpen = false) {
        const { state, commit } = context;
        try {
            const response = await ProjectsAPI.reorderSections(state.project._id, sections);
            commit("replaceProject", response.data);
            updatePageInList(context);
        } catch (error) {
            console.error(error);
        } finally {
            if (!keepModalOpen) {
                commit("setActiveModal", null);
            }
        }
    },

    // TODO this is a project level action, not section level
    async deleteProjectImage( { state }, { imageUrl, croppedImageUrl, options = {} }) {
        if (!imageUrl && !croppedImageUrl) return;
        try {
            await ProjectsAPI.deleteProjectImage(state.project._id, imageUrl, croppedImageUrl, options);
        } catch (error) {
            console.error(error);
        }
    },

    async deleteGalleryItem({ state, commit }, item) {
        try {
            const response = await ProjectsAPI.deleteGalleryItem(
                state.project._id,
                state.selectedSection.index,
                item
            );
            commit("updateGalleryItems", response.data.galleryItems);
            state.project.sections[state.selectedSection.index].galleryItems = Object.assign(
                [],
                response.data.galleryItems
            );
            commit("showAlertMessage", { type: "success", message: "Image successfully deleted." });
        } catch (error) {
            console.error(error);
        }
    },

    async duplicateSection(context, { sectionId }) {
        const { state, commit, getters } = context;
        if (getters.isEditorBlocked) return;

        const sourceSection = state.project.sections.find((s) => s._id === sectionId);
        if (!sourceSection) {
            console.warn("duplicateSection: source section not found in store", sectionId);
            return;
        }
        const insertIndex = sourceSection.index + 1;

        commit("updateSavingState");

        try {
            const response = await ProjectsAPI.duplicateSection(
                state.project._id,
                sectionId,
                insertIndex
            );

            commit("replaceProject", response.data);

            const newSection = response.data.sections[insertIndex];
            commit("selectSection", newSection);
            commit("scrollToSection", { sectionId: newSection?._id });

            updatePageInList(context);
            await updateNavSection(context, insertIndex, true);
            if (newSection?.copyStatus === "pending") {
                pollSectionCopyCompletion(context, newSection._id);
            }
        } catch (error) {
            console.error(error);
            const errorObject = error.response?.data;
            if (error.response?.status === 405 && errorObject?.code === 1014) {
                if (state.userSettings?.currentSubscription?.status === 'past_due') {
                    console.log("BLOCKED from: duplicateSection");
                    commit("setBlockedEditor", true);
                }
            }
        } finally {
            commit("updateSavingState");
            commit("setActiveModal", null);
        }
    },
};

export default SectionActions;

function pollSectionCopyCompletion(context, sectionId) {
    const { state, commit } = context;
    let attempts = 0;
    const maxAttempts = 30;
    const interval = setInterval(async () => {
        attempts += 1;
        try {
            const response = await ProjectsApi.getProject(state.project._id);
            commit("replaceProject", response.data);
            const updatedSection = response.data.sections.find((s) => s._id === sectionId);
            if (!updatedSection?.copyStatus || attempts >= maxAttempts) {
                clearInterval(interval);
                if (updatedSection) {
                    commit("selectSection", updatedSection);
                }
            }
        } catch (e) {
            console.error("Failed to poll section copy status", e);
            if (attempts >= maxAttempts) {
                clearInterval(interval);
            }
        }
    }, 1000);
}
